import React from 'react';
import PropTypes from 'prop-types';
import NumberFormat from 'react-number-format';

import { App } from '@meronex/app';
import IOut from '@meronex/icons/bi/BiExport';
import IIn from '@meronex/icons/bi/BiImport';
import IReverse from '@meronex/icons/fa/FaUndo';
import IEdit from '@meronex/icons/ai/AiTwotoneEdit';
import IFactory from '@meronex/icons/mdc/MdcFactory';
import ITransfer from '@meronex/icons/bs/BsArrowRight';
import INote from '@meronex/icons/fd/FdClipboardNotes';

import { Icons } from '../../../../common/Icons';
import { helpers } from '../../../../common/helpers';
import SitesAvatar from '../../../../common/SitesAvatar';
import SitesUserName from '../../../../common/SitesUserName';

const TRANSACTION_TYPES = helpers.TRANSACTION_TYPES;

function TransactionItem(props) {
  const { item, index, onClick, style, isStoreTransaction, showPrice, isDark } = props;

  if (!item) {
    return null;
  }

  const storeItem = item.storeItem;
  const unitLabel = storeItem.type.unit;

  const redColor = isDark ? '#f87171' : '#dc2626';
  const greenColor = '#16a34a';
  const grayColor = isDark ? '#64748b' : '#9ca3af';
  const blueColor = isDark ? '#818cf8' : '#4f46e5';
  const mutedColor = isDark ? '#64748b' : '#94a3b8';

  const itemData = { icon: undefined, color: undefined, label: '', bgColor: undefined };

  if (item.transactionType === TRANSACTION_TYPES.out) {
    itemData.icon = <IOut size={13} />;
    itemData.color = redColor;
    itemData.bgColor = isDark ? 'rgba(248,113,113,0.1)' : '#fef2f2';
    itemData.label = 'Out';
  } else if (item.transactionType === TRANSACTION_TYPES.transferOut) {
    if (item.workOrderId) {
      itemData.icon = <><IFactory size={13} /> <IOut size={13} /></>;
      itemData.color = redColor;
      itemData.bgColor = isDark ? 'rgba(248,113,113,0.1)' : '#fef2f2';
      itemData.label = 'Material out';
    } else {
      itemData.icon = <><Icons.Transfer size={13} /> <IOut size={13} /></>;
      itemData.color = redColor;
      itemData.bgColor = isDark ? 'rgba(248,113,113,0.1)' : '#fef2f2';
      itemData.label = 'Transfer out';
    }
  }
  if (item.transactionType === TRANSACTION_TYPES.in) {
    if (item.workOrderId) {
      itemData.icon = <><IFactory size={13} /> <IIn size={13} /></>;
      itemData.color = greenColor;
      itemData.bgColor = isDark ? 'rgba(22,163,74,0.1)' : '#f0fdf4';
      itemData.label = 'Product in';
    } else {
      itemData.icon = <IIn size={13} />;
      itemData.color = greenColor;
      itemData.bgColor = isDark ? 'rgba(22,163,74,0.1)' : '#f0fdf4';
      itemData.label = 'In';
    }
  } else if (item.transactionType === TRANSACTION_TYPES.transferIn) {
    itemData.icon = <><Icons.Transfer size={13} /> <IIn size={13} /></>;
    itemData.color = greenColor;
    itemData.bgColor = isDark ? 'rgba(22,163,74,0.1)' : '#f0fdf4';
    itemData.label = 'Transfer in';
  } else if (item.transactionType === TRANSACTION_TYPES.reverse) {
    itemData.icon = <IReverse size={11} />;
    itemData.color = grayColor;
    itemData.bgColor = isDark ? 'rgba(255,255,255,0.06)' : '#f8fafc';
    itemData.label = 'Reverse';
  } else if (item.transactionType === TRANSACTION_TYPES.modify) {
    itemData.icon = <IEdit size={13} />;
    itemData.color = blueColor;
    itemData.bgColor = isDark ? 'rgba(129,140,248,0.1)' : '#eef2ff';
    itemData.label = 'Modify';
  }

  const imgSrc = ((storeItem.type || {}).image || {}).thumbUrl;
  const thousandsSeparator = App.utils.number.thousandsSeparator;
  const round = App.utils.number.round;
  const formatQuantity = (amount) => thousandsSeparator(round(amount));

  return (
    <div
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        padding: '10px 14px',
        cursor: onClick ? 'pointer' : 'default',
        borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9'}`,
        ...style,
      }}>

      {/* Left: type pill */}
      <div style={{
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '3px',
        padding: '4px 8px',
        borderRadius: '6px',
        backgroundColor: itemData.bgColor,
        color: itemData.color,
        marginRight: '10px',
      }}>
        {itemData.icon}
      </div>

      {/* Middle: info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {/* Row 1: item name (store transactions) or type label + time */}
        {isStoreTransaction && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            marginBottom: '2px',
          }}>
            <SitesAvatar
              image={imgSrc}
              name={storeItem.type.name}
              size={18}
              round={4}
            />
            <span style={{
              fontWeight: 600,
              fontSize: '13px',
              color: isDark ? '#e2e8f0' : '#0f172a',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
              {storeItem.type.name}
            </span>
          </div>
        )}

        {/* Row 2: type label + relative time */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          fontSize: '12px',
        }}>
          <span style={{ color: itemData.color, fontWeight: 500 }}>
            {itemData.label}
          </span>
          {item.reversed && (
            <span style={{
              fontSize: '10px',
              color: grayColor,
              padding: '0 4px',
              borderRadius: '3px',
              backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9',
            }}>
              reversed
            </span>
          )}
          <span style={{ color: mutedColor }}>
            · {App.utils.time.timeAgo(item.createdAt, true)}
          </span>
          {item.note && <INote style={{ color: mutedColor, fontSize: '11px' }} />}
        </div>

        {/* Row 3: by user + date */}
        <div style={{
          fontSize: '11px',
          color: mutedColor,
          marginTop: '1px',
        }}>
          {item.createdBy && <SitesUserName profile={item.createdBy} />}
          <span style={{ marginLeft: '6px' }}>
            {App.utils.time.timeFullFormat(item.createdAt, true)}
          </span>
        </div>

        {/* Optional details */}
        {item.group && Object.keys(item.group).length !== 0 && (
          <div style={{ fontSize: '11px', color: mutedColor, marginTop: '3px' }}>
            <Icons.Batch size={11} />
            {' '}{item.group.name}{' '}
            {formatQuantity(item.group.quantity)}
            {' '}<ITransfer size={9} />{' '}
            {formatQuantity(item.group.quantity + item.amount)} {storeItem.type.unit}
          </div>
        )}
        {showPrice && item.adjustedUnitRate && item.adjustedUnitRate.transactionUnitRate && (
          <div style={{ fontSize: '11px', color: mutedColor, marginTop: '3px' }}>
            Rate: {item.adjustedUnitRate.transactionUnitRate.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} AED/{storeItem.type.unit}
            {' · '}Value: {formatQuantity(item.amount * item.adjustedUnitRate.transactionUnitRate)} AED
          </div>
        )}
        {showPrice && item.cost && (
          <div style={{ fontSize: '11px', color: mutedColor, marginTop: '3px' }}>
            Cost: {item.cost.total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} AED
          </div>
        )}
        {item.from && item.from.type && item.from.store && item.to && item.to.type && item.to.store && (
          <div style={{ fontSize: '11px', color: mutedColor, marginTop: '3px' }}>
            {(item.from.store || {}).name} ({formatQuantity(item.from.quantity)} - {formatQuantity(Math.abs(item.amount))})
            {' '}<ITransfer size={9} />{' '}
            {item.to.store.name} ({formatQuantity(item.to.quantity)} + {formatQuantity(Math.abs(item.amount))})
          </div>
        )}
      </div>

      {/* Right: quantity */}
      <div style={{
        flexShrink: 0,
        textAlign: 'right',
        marginLeft: '10px',
        minWidth: '48px',
      }}>
        <div style={{
          fontSize: '16px',
          fontWeight: 700,
          color: itemData.color,
          lineHeight: 1.2,
        }}>
          {item.amount > 0 && item.transactionType !== 'modify' && '+'}
          <NumberFormat
            value={item.amount}
            displayType={'text'}
            thousandSeparator={true}
          />
        </div>
        <div style={{
          fontSize: '10px',
          fontWeight: 500,
          color: mutedColor,
          textTransform: 'uppercase',
          letterSpacing: '0.3px',
          lineHeight: 1.4,
        }}>
          {unitLabel}
        </div>
        <div style={{
          fontSize: '11px',
          color: isDark ? '#475569' : '#cbd5e1',
          lineHeight: 1.4,
          marginTop: '1px',
        }}>
          {formatQuantity(item.storeItem.quantity)}
        </div>
      </div>
    </div>
  );
}
TransactionItem.propTypes = {
  item: PropTypes.object,
  onClick: PropTypes.func,
  isStoreTransaction: PropTypes.bool,
};

TransactionItem.defaultProps = {
  style: {},
  isStoreTransaction: false,
};
export default TransactionItem;
